"use server"

import { sdk } from "@lib/config"
import { HttpTypes } from "@medusajs/types"
import { revalidatePath, revalidateTag } from "next/cache"
import { redirect } from "next/navigation"
import {
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  removeCartId,
  setCartId,
} from "./cookies"
import { getRegion } from "./regions"
import { getLocale } from "@lib/data/locale-actions"

/**
 * Retrieves a cart by its ID. If no ID is provided, it will use the cart ID from the cookies.
 * @param cartId - optional - The ID of the cart to retrieve.
 * @returns The cart object if found, or null if not found.
 */
export async function retrieveCart(cartId?: string, fields?: string) {
  const id = cartId || (await getCartId())
  fields ??=
    "*items, *region, *region.countries, *items.product, *items.variant, *items.thumbnail, *items.metadata, +items.total, *promotions, +shipping_methods.name"

  if (!id) {
    return null
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("carts")),
  }

  return await sdk.client
    .fetch<HttpTypes.StoreCartResponse>(`/store/carts/${id}`, {
      method: "GET",
      query: {
        fields,
      },
      headers,
      next,
      cache: "force-cache",
    })
    .then(({ cart }: { cart: HttpTypes.StoreCart }) => cart)
    .catch(() => null)
}

export async function getOrSetCart(countryCode: string) {
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`)
  }

  let cart = await retrieveCart(undefined, "id,region_id")

  const headers = {
    ...(await getAuthHeaders()),
  }

  if (!cart) {
    const locale = await getLocale()
    const cartResp = await sdk.store.cart.create(
      { region_id: region.id, locale: locale || undefined },
      {},
      headers
    )
    cart = cartResp.cart

    await setCartId(cart.id)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  if (cart && cart?.region_id !== region.id) {
    await sdk.store.cart.update(cart.id, { region_id: region.id }, {}, headers)
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  return cart
}

export async function updateCart(data: HttpTypes.StoreUpdateCart) {
  const cartId = await getCartId()

  if (!cartId) {
    return { success: false as const, error: "No existing cart found" }
  }

  try {
    const headers = {
      ...(await getAuthHeaders()),
    }

    const { cart } = await sdk.store.cart.update(cartId, data, {}, headers)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)

    const fulfillmentCacheTag = await getCacheTag("fulfillment")
    revalidateTag(fulfillmentCacheTag)

    return { success: true as const, cart }
  } catch (error: any) {
    return { success: false as const, error: error.message || "Failed to update cart" }
  }
}

export async function addToCart({
  variantId,
  quantity,
  countryCode,
}: {
  variantId: string
  quantity: number
  countryCode: string
}): Promise<{ success: boolean; error?: string }> {
  if (!variantId) {
    return { success: false, error: "Missing variant ID when adding to cart" }
  }

  try {
    const cart = await getOrSetCart(countryCode)

    if (!cart) {
      return { success: false, error: "Error retrieving or creating cart" }
    }

    const headers = {
      ...(await getAuthHeaders()),
    }

    await sdk.store.cart.createLineItem(
      cart.id,
      {
        variant_id: variantId,
        quantity,
      },
      {},
      headers
    )

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
    // Header cart badge lives in the shared (main) layout, fetched with
    // force-cache. Guests whose _medusa_cache_id cookie wasn't set until
    // this request get an untagged, URL-cached fetch that revalidateTag
    // can't reach — see initiatePaymentSession's /checkout fix for the
    // same class of bug.
    revalidatePath("/", "layout")

    const fulfillmentCacheTag = await getCacheTag("fulfillment")
    revalidateTag(fulfillmentCacheTag)

    return { success: true }
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to add to cart"
    return { success: false, error: message }
  }
}

/**
 * Adds a variant to the cart together with any personalisation the customer
 * entered (name & number printing, patch). Hits the custom
 * /store/carts/:id/customization route (see admin-medusa
 * addToCartWithCustomizationWorkflow) instead of the plain SDK line-item
 * create, so the personalisation fee line items and metadata are persisted —
 * the generic addToCart() has no way to carry this data.
 */
export async function addToCartWithCustomization({
  variantId,
  quantity,
  countryCode,
  customName,
  customNumber,
  patchId,
}: {
  variantId: string
  quantity: number
  countryCode: string
  customName?: string
  customNumber?: string
  patchId?: string
}): Promise<{ success: boolean; error?: string }> {
  if (!variantId) {
    return { success: false, error: "Missing variant ID when adding to cart" }
  }

  try {
    const cart = await getOrSetCart(countryCode)

    if (!cart) {
      return { success: false, error: "Error retrieving or creating cart" }
    }

    const headers = {
      ...(await getAuthHeaders()),
    }

    await sdk.client.fetch(`/store/carts/${cart.id}/customization`, {
      method: "POST",
      body: {
        variant_id: variantId,
        quantity,
        custom_name: customName || undefined,
        custom_number: customNumber || undefined,
        patch_id: patchId && patchId !== "none" ? patchId : undefined,
      },
      headers,
    })

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
    // See addToCart above — busts the header cart badge for guests whose
    // fetch was cached before _medusa_cache_id existed.
    revalidatePath("/", "layout")

    const fulfillmentCacheTag = await getCacheTag("fulfillment")
    revalidateTag(fulfillmentCacheTag)

    return { success: true }
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to add to cart"
    return { success: false, error: message }
  }
}

/**
 * Edits the customisation (name, number, patch) on an existing cart line
 * item in place via the admin-medusa /store/carts/:id/customization PATCH
 * route (see updateCartItemCustomizationWorkflow) — replaces the metadata
 * and the linked fee line items instead of creating a duplicate.
 */
export async function updateCartItemCustomization({
  lineItemId,
  customName,
  customNumber,
  patchId,
}: {
  lineItemId: string
  customName?: string
  customNumber?: string
  patchId?: string
}): Promise<{ success: boolean; error?: string }> {
  const cartId = await getCartId()

  if (!cartId) {
    return { success: false, error: "No existing cart found" }
  }

  try {
    const headers = {
      ...(await getAuthHeaders()),
    }

    await sdk.client.fetch(`/store/carts/${cartId}/customization`, {
      method: "PATCH",
      body: {
        line_item_id: lineItemId,
        custom_name: customName || undefined,
        custom_number: customNumber || undefined,
        patch_id: patchId && patchId !== "none" ? patchId : undefined,
      },
      headers,
    })

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)

    const fulfillmentCacheTag = await getCacheTag("fulfillment")
    revalidateTag(fulfillmentCacheTag)

    return { success: true }
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to update customisation"
    return { success: false, error: message }
  }
}

export async function updateLineItem({
  lineId,
  quantity,
}: {
  lineId: string
  quantity: number
}) {
  if (!lineId) {
    return { success: false, error: "Missing lineItem ID when updating line item" }
  }

  const cartId = await getCartId()

  if (!cartId) {
    return { success: false, error: "Missing cart ID when updating line item" }
  }

  try {
    const headers = {
      ...(await getAuthHeaders()),
    }

    await sdk.store.cart.updateLineItem(cartId, lineId, { quantity }, {}, headers)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
    // See addToCart above — busts the header cart badge for guests whose
    // fetch was cached before _medusa_cache_id existed.
    revalidatePath("/", "layout")

    const fulfillmentCacheTag = await getCacheTag("fulfillment")
    revalidateTag(fulfillmentCacheTag)

    return { success: true }
  } catch (error: any) {
    return { success: false, error: error.message || "Failed to update line item" }
  }
}

export async function deleteLineItem(lineId: string) {
  if (!lineId) {
    return { success: false, error: "Missing lineItem ID when deleting line item" }
  }

  const cartId = await getCartId()

  if (!cartId) {
    return { success: false, error: "Missing cart ID when deleting line item" }
  }

  try {
    const headers = {
      ...(await getAuthHeaders()),
    }

    await sdk.store.cart.deleteLineItem(cartId, lineId, {}, headers)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
    // See addToCart above — busts the header cart badge for guests whose
    // fetch was cached before _medusa_cache_id existed.
    revalidatePath("/", "layout")

    const fulfillmentCacheTag = await getCacheTag("fulfillment")
    revalidateTag(fulfillmentCacheTag)

    return { success: true }
  } catch (error: any) {
    return { success: false, error: error.message || "Failed to delete line item" }
  }
}

export async function setShippingMethod({
  cartId,
  shippingMethodId,
}: {
  cartId: string
  shippingMethodId: string
}) {
  try {
    const headers = {
      ...(await getAuthHeaders()),
    }

    await sdk.store.cart.addShippingMethod(
      cartId,
      { option_id: shippingMethodId },
      {},
      headers
    )

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)

    return { success: true }
  } catch (error: any) {
    return { success: false, error: error.message || "Failed to set shipping method" }
  }
}

export async function initiatePaymentSession(
  cart: HttpTypes.StoreCart,
  data: HttpTypes.StoreInitializePaymentSession
) {
  try {
    const headers = {
      ...(await getAuthHeaders()),
    }

    const resp = await sdk.store.payment.initiatePaymentSession(
      cart,
      data,
      {},
      headers
    )

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
    // Force checkout page to re-fetch cart regardless of per-user cache tag.
    // Without this, users without a _medusa_cache_id cookie get stale cart
    // data after router.refresh(), causing PaymentWrapper to miss the new session.
    revalidatePath("/checkout", "page")

    return { success: true, data: resp }
  } catch (error: any) {
    return { success: false, error: error.message || "Failed to initiate payment session" }
  }
}

export async function applyPromotions(codes: string[]) {
  const cartId = await getCartId()

  if (!cartId) {
    return { success: false, error: "No existing cart found" }
  }

  try {
    const headers = {
      ...(await getAuthHeaders()),
    }

    await sdk.store.cart.update(cartId, { promo_codes: codes }, {}, headers)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)

    return { success: true }
  } catch (error: any) {
    return { success: false, error: error.message || "Failed to apply promotions" }
  }
}

export async function submitPromotionForm(
  currentState: unknown,
  formData: FormData
) {
  const code = formData.get("code") as string
  return applyPromotions([code])
}

// TODO: Pass a POJO instead of a form entity here
export async function setAddresses(currentState: unknown, formData: FormData) {
  if (!formData) {
    return { success: false, error: "No form data found when setting addresses" }
  }

  const cartId = await getCartId()
  if (!cartId) {
    return { success: false, error: "No existing cart found when setting addresses" }
  }

  try {
    const data = {
      shipping_address: {
        first_name: formData.get("shipping_address.first_name"),
        last_name: formData.get("shipping_address.last_name"),
        address_1: formData.get("shipping_address.address_1"),
        address_2: formData.get("shipping_address.address_2"),
        postal_code: formData.get("shipping_address.postal_code"),
        city: formData.get("shipping_address.city"),
        country_code: formData.get("shipping_address.country_code"),
        province: formData.get("shipping_address.province"),
        phone: formData.get("shipping_address.phone"),
      },
      billing_address: {
        first_name: formData.get("billing_address.first_name"),
        last_name: formData.get("billing_address.last_name"),
        address_1: formData.get("billing_address.address_1"),
        address_2: formData.get("billing_address.address_2"),
        postal_code: formData.get("billing_address.postal_code"),
        city: formData.get("billing_address.city"),
        country_code: formData.get("billing_address.country_code"),
        province: formData.get("billing_address.province"),
        phone: formData.get("billing_address.phone"),
      },
      email: formData.get("email"),
    } as any

    const result = await updateCart(data)
    if (!result.success) {
      return { success: false, error: result.error }
    }

    return { success: true as const }
  } catch (e: any) {
    return { success: false, error: e.message || "Failed to set addresses" }
  }
}

/**
 * Places an order for a cart. If no cart ID is provided, it will use the cart ID from the cookies.
 * Returns { success: false, error } on failure so callers can show a user-friendly message.
 * On success it redirects server-side to the order confirmation page.
 */
export async function placeOrder(
  cartId?: string
): Promise<{ success: false; error: string }> {
  const id = cartId || (await getCartId())

  if (!id) {
    return { success: false, error: "No existing cart found. Please refresh and try again." }
  }

  try {
    const headers = {
      ...(await getAuthHeaders()),
    }

    const cartRes = await sdk.store.cart.complete(id, {}, headers)

    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)

    if (cartRes?.type === "order") {
      const countryCode =
        cartRes.order.shipping_address?.country_code?.toLowerCase()

      const orderCacheTag = await getCacheTag("orders")
      revalidateTag(orderCacheTag)

      removeCartId()
      redirect(`/order/${cartRes?.order.id}/confirmed`)
    }

    // Cart completion did not produce an order (e.g. payment not captured)
    return { success: false, error: "Order could not be completed. Please check your payment and try again." }
  } catch (error: any) {
    // redirect() throws a NEXT_REDIRECT error internally — re-throw it
    if (error?.digest?.startsWith("NEXT_REDIRECT")) {
      throw error
    }
    return { success: false, error: error.message || "Failed to place order" }
  }
}

/**
 * Updates the countrycode param and revalidates the regions cache
 * @param regionId
 * @param countryCode
 */
export async function updateRegion(countryCode: string, currentPath: string) {
  const cartId = await getCartId()
  const region = await getRegion(countryCode)

  if (!region) {
    return { success: false, error: `Region not found for country code: ${countryCode}` }
  }

  if (cartId) {
    const result = await updateCart({ region_id: region.id })
    if (!result.success) {
      return { success: false, error: result.error }
    }
    const cartCacheTag = await getCacheTag("carts")
    revalidateTag(cartCacheTag)
  }

  const regionCacheTag = await getCacheTag("regions")
  revalidateTag(regionCacheTag)

  const productsCacheTag = await getCacheTag("products")
  revalidateTag(productsCacheTag)

  redirect(currentPath || "/")
}

export async function listCartOptions() {
  const cartId = await getCartId()
  const headers = {
    ...(await getAuthHeaders()),
  }
  const next = {
    ...(await getCacheOptions("shippingOptions")),
  }

  return await sdk.client.fetch<{
    shipping_options: HttpTypes.StoreCartShippingOption[]
  }>("/store/shipping-options", {
    query: { cart_id: cartId },
    next,
    headers,
    cache: "force-cache",
  })
}

/**
 * Creates an isolated express cart for PayPal Express Checkout from the product page.
 * This is NOT the customer's main shopping cart — it is a separate, single-item cart
 * used only for the PayPal express flow. Do not call setCartId() or revalidateTag()
 * here because we do not want to replace the customer's active cart.
 */
/**
 * Sets a placeholder UK shipping address and auto-selects the cheapest shipping
 * method on an express cart BEFORE payment session initiation. This ensures the
 * PayPal order amount includes shipping from the start, so the payment session
 * remains valid when the real address is applied after buyer approval.
 * Returns the updated cart (needed for initiatePaymentSession).
 */
export async function setupExpressCartShipping(
  cartId: string
): Promise<{ success: true; cart: HttpTypes.StoreCart } | { success: false; error: string }> {
  try {
    const headers = { ...(await getAuthHeaders()) }

    // Placeholder UK address — needed so Medusa can calculate shipping options.
    // The real address is applied in updateExpressCartAddress after PayPal approval.
    await sdk.store.cart.update(
      cartId,
      {
        email: "express@checkout.placeholder",
        shipping_address: {
          first_name: "Express",
          last_name: "Buyer",
          address_1: "1 High Street",
          city: "London",
          postal_code: "SW1A 1AA",
          country_code: "gb",
          phone: "",
        },
      } as any,
      {},
      headers
    )

    const { shipping_options } = await sdk.client.fetch<{
      shipping_options: HttpTypes.StoreCartShippingOption[]
    }>("/store/shipping-options", {
      query: { cart_id: cartId },
      headers,
      cache: "no-store",
    })

    if (!shipping_options?.length) {
      return { success: false, error: "No shipping options available." }
    }

    const cheapest = shipping_options.reduce((a, b) =>
      (a.amount ?? Infinity) <= (b.amount ?? Infinity) ? a : b
    )

    await sdk.store.cart.addShippingMethod(
      cartId,
      { option_id: cheapest.id },
      {},
      headers
    )

    // Re-fetch so the returned cart has the updated total (including shipping)
    const { cart } = await sdk.store.cart.retrieve(cartId, {}, headers)
    return { success: true, cart }
  } catch (error: any) {
    return { success: false, error: error.message || "Failed to set up shipping." }
  }
}

/**
 * Replaces the placeholder address on an express cart with the buyer's real
 * address from PayPal. Only updates address fields — does NOT touch the
 * shipping method — so the cart total and payment session remain unchanged.
 */
export async function updateExpressCartAddress(
  cartId: string,
  address: {
    email: string
    firstName: string
    lastName: string
    address1: string
    city: string
    postalCode: string
    countryCode: string
    phone?: string
  }
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const headers = { ...(await getAuthHeaders()) }

    await sdk.store.cart.update(
      cartId,
      {
        email: address.email,
        shipping_address: {
          first_name: address.firstName,
          last_name: address.lastName,
          address_1: address.address1,
          city: address.city,
          postal_code: address.postalCode,
          country_code: address.countryCode.toLowerCase(),
          phone: address.phone || "",
        },
        billing_address: {
          first_name: address.firstName,
          last_name: address.lastName,
          address_1: address.address1,
          city: address.city,
          postal_code: address.postalCode,
          country_code: address.countryCode.toLowerCase(),
          phone: address.phone || "",
        },
      } as any,
      {},
      headers
    )

    return { success: true }
  } catch (error: any) {
    return { success: false, error: error.message || "Failed to update shipping address." }
  }
}

/**
 * Completes an express checkout cart and returns the resulting order ID.
 * Unlike placeOrder(), this function does NOT call redirect() — the caller
 * is responsible for navigation. This is required when called from inside
 * a third-party SDK callback (e.g. PayPal's onApprove) where Next.js cannot
 * intercept the NEXT_REDIRECT error thrown by redirect().
 */
export async function completeExpressCart(
  cartId: string
): Promise<{ success: true; orderId: string } | { success: false; error: string }> {
  try {
    const headers = { ...(await getAuthHeaders()) }
    const cartRes = await sdk.store.cart.complete(cartId, {}, headers)

    if (cartRes?.type === "order") {
      const orderCacheTag = await getCacheTag("orders")
      revalidateTag(orderCacheTag)
      return { success: true, orderId: cartRes.order.id }
    }

    return {
      success: false,
      error: "Order could not be completed. Please check your payment and try again.",
    }
  } catch (error: any) {
    return { success: false, error: error.message || "Failed to place order" }
  }
}

export async function createExpressCart(
  variantId: string,
  quantity: number,
  countryCode: string
): Promise<HttpTypes.StoreCart> {
  const region = await getRegion(countryCode)

  if (!region) {
    throw new Error(`No region found for country code: ${countryCode}`)
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const { cart } = await sdk.store.cart.create(
    { region_id: region.id },
    {},
    headers
  )

  await sdk.store.cart.createLineItem(
    cart.id,
    { variant_id: variantId, quantity },
    {},
    headers
  )

  const { cart: fullCart } = await sdk.store.cart.retrieve(
    cart.id,
    {},
    headers
  )

  return fullCart
}
